
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Extend Window interface to include our global API_RATE_LIMIT property
declare global {
  interface Window {
    API_RATE_LIMIT?: {
      lastRequest: number;
      inRateLimitCooldown: boolean;
      cooldownEndTime: number;
      consecutiveErrors: number;
      lastSuccessfulRequest: Record<string, number>;
      networkErrorCount: number;
      lastNetworkError: string | null;
      lastNetworkErrorTime: number;
    };
  }
}

interface CloseContextType {
  isConnected: boolean;
  apiKey: string;
  disconnect: () => void;
  setIsConnected: (connected: boolean) => void;
  setApiKey: (key: string) => void;
  userEmail: string | null;
  userName: string | null;
  setUserEmail: (email: string | null) => void;
  setUserName: (name: string | null) => void;
}

const CloseContext = createContext<CloseContextType | undefined>(undefined);

export const CloseProvider = ({ children }: { children: ReactNode }) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [apiKey, setApiKey] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  
  // Initialize with stored key on component mount
  useEffect(() => {
    console.log("Initializing Close context with stored data");
    const storedKey = localStorage.getItem('close_api_key');
    const storedEmail = localStorage.getItem('close_user_email');
    const storedName = localStorage.getItem('close_user_name');
    
    if (storedKey) {
      console.log("Using stored API key");
      setApiKey(storedKey);
      setIsConnected(true);
      
      if (storedEmail) {
        setUserEmail(storedEmail);
      }
      
      if (storedName) {
        setUserName(storedName);
      }
    } else {
      console.log("No API key found");
    }
  }, []);

  const disconnect = () => {
    console.log("Disconnecting from Close CRM");
    setIsConnected(false);
    setApiKey("");
    setUserEmail(null);
    setUserName(null);
    
    // Clear all Close CRM related data from localStorage
    localStorage.removeItem('close_api_key');
    localStorage.removeItem('close_user_email');
    localStorage.removeItem('close_user_name');
    localStorage.removeItem('close_metrics_cache');
    localStorage.removeItem('close_metrics_cache_time');
    localStorage.removeItem('close_metrics_last_request');
    
    // Reset rate limiting state
    try {
      // If we can access the rate limit object from closeApiRateLimiting.ts
      if (window && window.API_RATE_LIMIT) {
        window.API_RATE_LIMIT = {
          lastRequest: 0,
          inRateLimitCooldown: false,
          cooldownEndTime: 0,
          consecutiveErrors: 0,
          lastSuccessfulRequest: {},
          networkErrorCount: 0,
          lastNetworkError: null,
          lastNetworkErrorTime: 0
        };
      }
    } catch (error) {
      console.error("Failed to reset rate limiting state:", error);
    }
  };

  return (
    <CloseContext.Provider value={{ 
      isConnected, 
      apiKey, 
      disconnect,
      setIsConnected,
      setApiKey,
      userEmail,
      userName,
      setUserEmail,
      setUserName
    }}>
      {children}
    </CloseContext.Provider>
  );
};

export const useClose = () => {
  const context = useContext(CloseContext);
  if (context === undefined) {
    throw new Error('useClose must be used within a CloseProvider');
  }
  return context;
};
